import { memo, useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme, Button, Text, Spacer, Link, Icon, Avatar, Popover } from '@Sage/hoi-poi-ui';
import moment from 'moment';
import { rrulestr } from 'rrule';

import { EntityListActions, EntityModalActions } from 'actions';
import Context from 'managers/Context';
import { AGENDA, COMPANIES, CONTACTS } from 'constants/Entities';
import { AGENDA_EVENT, ATTENDEE_TYPES } from 'constants/Constants';
import AgendaModel from 'models/AgendaModel';
import {
    formatDateToBackendUTC,
    getPreviousAndNextDate,
    getActivityDateFormat,
    getDateToTimezone,
} from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getRoute, ensureRoute } from 'utils/routes';
import { getUserSfmUrl, getCompanyDetailUrl } from 'utils/getUrl';
import { logEvent } from 'utils/tracking';
import { getNewStrRuleWithOffset } from 'utils/rrule';
import { getBackendBoolean } from 'utils/fm';
import { getSrcUserCircleAvatar, getSrcContactCircleAvatar } from 'utils/getSrcAvatar';

import { subscribe } from 'lib/EventBuser';
import { ENTITY_MODAL_UPDATE } from 'lib/events';

import useEntityDetail from 'containers/components/EntityDetail/hooks/useEntityDetail';
import CompanyChip from 'containers/components/chips/CompanyChip';
import DashboardWidget from '../components/DashboardWidget';
import DashboardWidgetHeader from '../components/DashboardWidget/DashboardWidgetHeader';
import EntityDetailPopover from 'containers/components/EntityDetailPopover';
import AgendaSmallDetail from 'containers/agenda/components/AgendaSmallDetail';
import DashboardWidgetRow from '../components/DashboardWidgetRow';
import CompanyAvatar from 'containers/components/avatars/CompanyAvatar';
import AttendeesField from '../../agenda/components/AgendaSmallDetail/fields/AttendeesField';
import VideoCallField from '../../agenda/components/AgendaSmallDetail/fields/VideoCallField';
import AttendeesList from '../../agenda/components/AgendaSmallDetail/attendees/AttendeesList';

const EMPTY_SVG = (
    <svg width={168} height={144} fill="none">
        <g mask="url(#a)">
            <path
                fill="#ABABAB"
                d="M102.712 109.651h-7.868v11.897h7.868v-11.897ZM72.036 44.75h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071V46.82c0-1.144-.93-2.07-2.077-2.07ZM87.07 44.75h-5.413a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071V46.82c0-1.144-.93-2.07-2.077-2.07ZM102.108 44.75h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071V46.82c0-1.144-.93-2.07-2.077-2.07ZM117.143 44.75h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071V46.82c0-1.144-.93-2.07-2.077-2.07ZM132.176 44.75h-5.413a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.929 2.071 2.077 2.071h5.413a2.073 2.073 0 0 0 2.077-2.071V46.82a2.074 2.074 0 0 0-2.077-2.07ZM72.036 59.89h-5.414a2.074 2.074 0 0 0-2.077 2.072v5.563c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.07v-5.564c0-1.144-.93-2.072-2.077-2.072ZM87.07 59.89h-5.413a2.074 2.074 0 0 0-2.077 2.072v5.563c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.07v-5.564c0-1.144-.93-2.072-2.077-2.072ZM102.108 59.89h-5.414a2.074 2.074 0 0 0-2.077 2.072v5.563c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.07v-5.564c0-1.144-.93-2.072-2.077-2.072ZM117.143 59.89h-5.414a2.074 2.074 0 0 0-2.077 2.072v5.563c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.07v-5.564c0-1.144-.93-2.072-2.077-2.072ZM132.176 59.89h-5.413a2.074 2.074 0 0 0-2.077 2.072v5.563c0 1.144.929 2.071 2.077 2.071h5.413a2.073 2.073 0 0 0 2.077-2.07v-5.564a2.074 2.074 0 0 0-2.077-2.072ZM72.036 75.364h-5.414a2.074 2.074 0 0 0-2.077 2.07V83c0 1.143.93 2.07 2.077 2.07h5.414A2.074 2.074 0 0 0 74.113 83v-5.564c0-1.144-.93-2.071-2.077-2.071ZM87.07 75.364h-5.413a2.074 2.074 0 0 0-2.077 2.07V83c0 1.143.93 2.07 2.077 2.07h5.414A2.074 2.074 0 0 0 89.148 83v-5.564c0-1.144-.93-2.071-2.077-2.071ZM102.108 75.364h-5.414a2.074 2.074 0 0 0-2.077 2.07V83c0 1.143.93 2.07 2.077 2.07h5.414a2.074 2.074 0 0 0 2.077-2.07v-5.564c0-1.144-.93-2.071-2.077-2.071ZM117.143 75.364h-5.414a2.074 2.074 0 0 0-2.077 2.07V83c0 1.143.93 2.07 2.077 2.07h5.414A2.074 2.074 0 0 0 119.22 83v-5.564c0-1.144-.93-2.071-2.077-2.071ZM132.176 75.364h-5.413a2.074 2.074 0 0 0-2.077 2.07V83c0 1.143.929 2.07 2.077 2.07h5.413a2.073 2.073 0 0 0 2.077-2.07v-5.564a2.074 2.074 0 0 0-2.077-2.071ZM72.036 90.504h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071v-5.564c0-1.144-.93-2.071-2.077-2.071ZM87.07 90.504h-5.413a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071v-5.564c0-1.144-.93-2.071-2.077-2.071ZM102.108 90.504h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071v-5.564c0-1.144-.93-2.071-2.077-2.071ZM117.143 90.504h-5.414a2.074 2.074 0 0 0-2.077 2.071v5.564c0 1.144.93 2.071 2.077 2.071h5.414a2.074 2.074 0 0 0 2.077-2.071v-5.564c0-1.144-.93-2.071-2.077-2.071ZM132.176 90.504h-5.413a2.073 2.073 0 0 0-2.077 2.071v5.564c0 1.144.929 2.071 2.077 2.071h5.413a2.074 2.074 0 0 0 2.077-2.071v-5.564a2.073 2.073 0 0 0-2.077-2.071Z"
            />
            <path
                fill="#00D639"
                d="M132.177 100.212h-5.413a2.073 2.073 0 0 1-2.076-2.072v-5.563c0-1.145 1.278-1.015 2.426-1.015h4.268c1.147 0 2.873-.13 2.873 1.015v5.564a2.073 2.073 0 0 1-2.077 2.071h-.001Z"
            />
            <path
                fill="#919191"
                d="m52.965 123.193-31.647.159 29.379-100.84M142.715 24.209H54.523v13.543h88.192V24.209Z"
            />
            <path
                fill="#C6C6C6"
                d="M144.71 123.187a2.252 2.252 0 0 1-2.255-2.249V24.761H55.103v96.177a2.253 2.253 0 0 1-2.256 2.249 2.253 2.253 0 0 1-2.255-2.249V22.512c0-1.242 1.01-2.25 2.255-2.25h91.863a2.253 2.253 0 0 1 2.256 2.25v98.426a2.253 2.253 0 0 1-2.256 2.249Z"
            />
            <path
                fill="#1B1B1B"
                d="M62.527 30.455a2.32 2.32 0 0 0 2.323-2.317 2.32 2.32 0 0 0-2.323-2.317 2.32 2.32 0 0 0-2.324 2.317 2.32 2.32 0 0 0 2.324 2.317Z"
            />
            <path
                fill="#5E5E5E"
                d="M62.872 28.428a1.262 1.262 0 0 1-1.264-1.261c0-.697.566-1.261 1.264-1.261 2.562 0 4.648-2.08 4.648-4.634 0-2.555-2.086-4.634-4.648-4.634-2.194 0-4.108 1.552-4.55 3.69a1.264 1.264 0 0 1-2.477-.51c.684-3.303 3.64-5.7 7.027-5.7 3.957 0 7.177 3.21 7.177 7.155 0 3.945-3.22 7.156-7.177 7.156v-.001Z"
            />
            <path fill="#C6C6C6" d="m53.168 20.256-.002 1.419 8.066.008.002-1.419-8.066-.008Z" />
            <path
                fill="#1B1B1B"
                d="M80.894 30.455a2.32 2.32 0 0 0 2.324-2.317 2.32 2.32 0 0 0-2.324-2.317 2.32 2.32 0 0 0-2.324 2.317 2.32 2.32 0 0 0 2.324 2.317Z"
            />
            <path
                fill="#5E5E5E"
                d="M81.241 28.428a1.262 1.262 0 0 1-1.264-1.261c0-.697.566-1.261 1.264-1.261 2.563 0 4.647-2.08 4.647-4.634 0-2.555-2.084-4.634-4.647-4.634-2.194 0-4.108 1.552-4.55 3.69a1.264 1.264 0 0 1-2.477-.51c.684-3.303 3.64-5.7 7.027-5.7 3.957 0 7.177 3.21 7.177 7.155 0 3.945-3.22 7.156-7.177 7.156v-.001Z"
            />
            <path fill="#C6C6C6" d="m71.537 20.256-.002 1.419 8.068.008.002-1.419-8.068-.008Z" />
            <path
                fill="#1B1B1B"
                d="M99.388 30.455a2.32 2.32 0 0 0 2.323-2.317 2.32 2.32 0 0 0-2.323-2.317 2.32 2.32 0 0 0-2.324 2.317 2.32 2.32 0 0 0 2.324 2.317Z"
            />
            <path
                fill="#5E5E5E"
                d="M99.734 28.428a1.262 1.262 0 0 1-1.265-1.261c0-.697.566-1.261 1.265-1.261 2.562 0 4.647-2.08 4.647-4.634 0-2.555-2.085-4.634-4.647-4.634-2.194 0-4.109 1.552-4.551 3.69a1.264 1.264 0 0 1-2.477-.51c.684-3.303 3.64-5.7 7.028-5.7 3.956 0 7.176 3.21 7.176 7.155 0 3.945-3.22 7.156-7.176 7.156v-.001Z"
            />
            <path fill="#C6C6C6" d="m90.031 20.256-.002 1.419 8.068.008.002-1.419-8.068-.008Z" />
            <path
                fill="#1B1B1B"
                d="M117.853 30.454a2.32 2.32 0 0 0 2.324-2.317 2.32 2.32 0 0 0-2.324-2.317 2.32 2.32 0 0 0-2.324 2.317 2.32 2.32 0 0 0 2.324 2.317Z"
            />
            <path
                fill="#5E5E5E"
                d="M118.2 28.427a1.262 1.262 0 0 1-1.264-1.26c0-.697.566-1.261 1.264-1.261 2.563 0 4.648-2.08 4.648-4.634 0-2.555-2.085-4.634-4.648-4.634-2.194 0-4.108 1.552-4.551 3.69a1.263 1.263 0 0 1-1.493.98 1.26 1.26 0 0 1-.983-1.49c.684-3.303 3.64-5.702 7.027-5.702 3.957 0 7.177 3.21 7.177 7.156 0 3.945-3.22 7.155-7.177 7.155Z"
            />
            <path fill="#C6C6C6" d="m108.496 20.256-.002 1.419 8.066.008.002-1.419-8.066-.008Z" />
            <path
                fill="#1B1B1B"
                d="M136.347 30.454a2.32 2.32 0 0 0 2.324-2.317 2.32 2.32 0 0 0-2.324-2.317 2.32 2.32 0 0 0-2.324 2.317 2.32 2.32 0 0 0 2.324 2.317Z"
            />
            <path
                fill="#5E5E5E"
                d="M136.695 28.427a1.262 1.262 0 0 1-1.265-1.26c0-.697.566-1.261 1.265-1.261 2.562 0 4.647-2.08 4.647-4.634 0-2.555-2.085-4.634-4.647-4.634-2.195 0-4.109 1.552-4.551 3.69a1.265 1.265 0 0 1-2.477-.51c.684-3.303 3.64-5.702 7.028-5.702 3.956 0 7.176 3.21 7.176 7.156 0 3.945-3.22 7.155-7.176 7.155Z"
            />
            <path fill="#C6C6C6" d="m126.99 20.256-.002 1.419 8.067.008.001-1.419-8.066-.008Z" />
            <path
                fill="#4E4E4E"
                d="M122.928 129.887a.213.213 0 0 1-.125-.388l.822-.579a.215.215 0 0 1 .299.05.214.214 0 0 1-.051.298l-.822.58a.21.21 0 0 1-.123.039ZM130.611 129.857a.213.213 0 0 1-.125-.387l.822-.58a.214.214 0 0 1 .249.348l-.822.58a.212.212 0 0 1-.124.039Z"
            />
            <path
                fill="#4E4E4E"
                d="M129.773 130.165c-.743 0-1.39-.546-1.497-1.299l-2.439-17.336-1.339-4.806-.868 21.991a1.511 1.511 0 0 1-1.511 1.45h-.06a1.512 1.512 0 0 1-1.452-1.567l1.03-26.121c.008-.185.048-.366.12-.535.274-.638 1.037-1.383 2.286-1.383s2.028.742 2.286 1.479l.03.095 2.42 8.686c.017.063.031.129.041.194l2.453 17.434a1.51 1.51 0 0 1-1.5 1.718Z"
            />
            <path
                fill="#C6C6C6"
                d="m125.574 97.658-.076 2.862a1.78 1.78 0 0 1-1.624 1.723 1.782 1.782 0 0 1-1.803-1.088l-1.149-2.756 1.865-1.204 2.787.463Z"
            />
            <path
                fill="#FF4C4E"
                d="M122.121 94.977a.846.846 0 1 0 0-1.69.847.847 0 0 0-.848.845c0 .467.38.845.848.845ZM117.183 111.494a1.006 1.006 0 0 1-.967-1.294l2.077-6.923a1.01 1.01 0 0 1 1.255-.676c.534.159.836.72.678 1.251l-2.077 6.923a1.008 1.008 0 0 1-.966.717v.002ZM129.825 98.706a.705.705 0 0 1-.705-.691l-.061-3.856a.705.705 0 0 1 .693-.715h.012c.384 0 .698.307.705.692l.061 3.855a.704.704 0 0 1-.694.715h-.011Z"
            />
            <path
                fill="#FF4C4E"
                d="M129.682 94.876c.42 0 .762-.34.762-.76a.761.761 0 0 0-1.524 0c0 .42.341.76.762.76Z"
            />
            <path
                fill="#454545"
                d="M129.621 93.803a.215.215 0 0 1-.215-.214v-1.493a.215.215 0 0 1 .43 0v1.493a.215.215 0 0 1-.215.214Z"
            />
            <path
                fill="#5E5E5E"
                d="M135.2 93.067h-.052l-10.855-.653a.853.853 0 1 1 .105-1.702l10.854.653c.471.028.83.432.802.903a.854.854 0 0 1-.852.8l-.002-.001Z"
            />
            <path
                fill="#C6C6C6"
                d="M118.903 106.264a1.508 1.508 0 0 1-1.445-1.959c.425-1.359 1.845-5.856 2.224-6.645.645-1.345 2.297-2.018 3.25-2.144.773-.102 1.608-.04 2.629.195.729.168 1.936.38 2.918.546a1.511 1.511 0 0 1 1.307-.791 1.506 1.506 0 0 1 1.537 1.484l.017 1.03c.008.444-.184.87-.519 1.161a1.518 1.518 0 0 1-1.225.353c-.131-.02-3.231-.5-4.713-.841-.658-.152-1.151-.197-1.554-.144-.299.04-.814.301-.922.467-.201.433-1.206 3.511-2.058 6.23a1.513 1.513 0 0 1-1.443 1.06l-.003-.002Z"
            />
        </g>
    </svg>
);

const UpcomingNextEventDashboardWidget = memo(() => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { openTab } = useEntityDetail();

    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const popoverCallbacksRef = useRef({});
    const agendaPermission = useSelector(
        (state) => state?.config?.permission?.crud_permission?.[AGENDA.permission],
    );
    const nylasConfig = useSelector((state) => state.config?.nylas);
    const syncCalendarWithNylas = useSelector(
        (state) => state.config?.userData?.syncCalendarWithNylas || false,
    );
    const idUser = useSelector((state) => state?.config?.userData?.idUsuario);
    const idTimezone = useSelector((state) => state?.config?.userData?.idTimeZone);
    const userName = useSelector(
        (state) => `${state?.config?.userData?.nombre} ${state?.config?.userData?.apellidos}`,
    );

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [event, setEvent] = useState();
    const [events, setEvents] = useState();
    const [timezone, setTimezone] = useState();

    const getEvents = useCallback(() => {
        setLoading(true);
        setError(false);

        let startDate = moment().startOf('day');
        const endDate = startDate.clone();
        endDate.add(6, 'month');

        let filters = {
            fini: {
                id: 'fini',
                value: formatDateToBackendUTC(startDate.toDate()),
            },
            ffin: {
                id: 'ffin',
                value: formatDateToBackendUTC(endDate.toDate()),
            },
            taskFlag: {
                id: 'taskFlag',
                value: '2',
            },
            completed: {
                id: 'completed',
                value: '0',
            },
            useUTCDates: {
                id: 'useUTCDates',
                value: true,
            },
            idusuario: {
                value: [idUser],
                id: 'idusuario',
            },
            newAttendees: {
                id: 'newAttendees',
                value: true,
            },
        };

        Context.domainManager.getEntityList(
            AGENDA.entity,
            0,
            100,
            filters,
            '',
            '',
            '',
            false,
            (result) => {
                setLoading(false);

                let events = result.reduce((arr, item) => {
                    item = AgendaModel.standardToCrud(item);

                    if (!item.g_recurrence || item.g_recurrence === '') {
                        const startMoment = getDateToTimezone({
                            date: item.fini,
                            inputFormat: 'YYYY-MM-DD HH:mm',
                            timezone: timezone?.idianazone,
                            returnMoment: true,
                        });
                        const endMoment = getDateToTimezone({
                            date: item.hfin,
                            inputFormat: 'YYYY-MM-DD HH:mm',
                            timezone: timezone?.idianazone,
                            returnMoment: true,
                        });
                        arr.push({
                            ...item,
                            fini: startMoment.toDate(),
                            ffin: endMoment.toDate(),
                            hini: startMoment.toDate(),
                            hfin: endMoment.toDate(),
                        });
                    } else {
                        let initDateString = item.Fini;
                        let initDateFormat = 'YYYY-MM-DD';
                        if (item.Hini) {
                            initDateString = initDateString + ' ' + item.Hini;
                            initDateFormat = initDateFormat + ' ' + 'HH:mm';
                        }

                        const initDate = moment.utc(initDateString, initDateFormat).toDate();
                        let g_recurrence =
                            item.g_recurrence?.replace('RRULE:RRULE:FREQ', 'FREQ') ||
                            item.g_recurrence;
                        g_recurrence = g_recurrence?.replace('RRULE:FREQ', 'FREQ');
                        const newGRecurrence = getNewStrRuleWithOffset(g_recurrence);
                        let rule = rrulestr(newGRecurrence, {
                            dtstart: initDate,
                        });

                        let excludeDates = [];
                        if (item.g_recurrence_exception) {
                            let newGRecurrence2 = newGRecurrence;
                            newGRecurrence2 += `\n${item.g_recurrence_exception}`;
                            let rule2 = rrulestr(newGRecurrence2, {
                                dtstart: initDate,
                                forceset: true,
                                cache: false,
                            });

                            excludeDates = rule2.exdates();
                        }

                        const day0 = moment(0, 'X');

                        if (startDate && endDate) {
                            const dates = rule.between(startDate.toDate(), endDate.toDate());
                            for (let i = 0; i < dates.length; i++) {
                                let dateYearMonthDay = moment(dates[i]).format('YYYY-MM-DD');
                                let startTime = moment(item.fini).format('HH:mm');
                                let endTime = moment(item.hfin).format('HH:mm');
                                const startMoment = getDateToTimezone({
                                    date: `${dateYearMonthDay} ${startTime}`,
                                    inputFormat: 'YYYY-MM-DD HH:mm',
                                    timezone: timezone?.idianazone,
                                    returnMoment: true,
                                });

                                if (excludeDates?.length) {
                                    const excluded = excludeDates.find((date) => {
                                        if (!startMoment || !startMoment.isValid()) return false;
                                        const startDateObj = startMoment.toDate();
                                        return (
                                            startDateObj &&
                                            date.getFullYear() === startDateObj.getFullYear() &&
                                            date.getMonth() === startDateObj.getMonth() &&
                                            date.getDate() === startDateObj.getDate()
                                        );
                                    });
                                    if (excluded) continue;
                                }

                                const endMoment = getDateToTimezone({
                                    date: `${dateYearMonthDay} ${endTime}`,
                                    inputFormat: 'YYYY-MM-DD HH:mm',
                                    timezone: timezone?.idianazone,
                                    returnMoment: true,
                                });
                                const dateFormat = moment(dates[i]).format('DD/MM/YYYY');
                                let newDateIni;
                                let newDateFin;
                                newDateIni = moment(dateFormat, 'DD/MM/YYYY');
                                newDateFin = moment(dateFormat, 'DD/MM/YYYY');

                                if (startMoment.isAfter(moment())) {
                                    arr.push({
                                        ...item,
                                        fini: startMoment.toDate(),
                                        hini: startMoment.toDate(),
                                        ffin: endMoment.toDate(),
                                        hfin: endMoment.toDate(),
                                        Fini: newDateIni.format('YYYY-MM-DD'),
                                        Fini_timestamp: newDateIni.diff(day0, 'seconds').toString(),
                                        Ffin: newDateFin.format('YYYY-MM-DD'),
                                        Ffin_timestamp: newDateFin.diff(day0, 'seconds').toString(),
                                    });
                                }
                            }
                        }
                    }
                    return arr;
                }, []);

                events = events
                    .sort((a, b) => {
                        if (moment(a.fini).isBefore(moment(b.fini))) {
                            return -1;
                        } else if (moment(a.fini).isAfter(moment(b.fini))) {
                            return 1;
                        } else {
                            return 0;
                        }
                    })
                    .filter((event) => {
                        if (moment(event.ffin).isBefore(moment())) return false;
                        return true;
                    });

                const upcomingEvent = events.filter((event) => {
                    if (getBackendBoolean(event.isTodoDia)) return false;
                    return true;
                })[0];

                setEvent(upcomingEvent);
                setEvents(events.filter((event) => event.Id !== upcomingEvent?.Id).slice(0, 100));
            },
            (error) => {
                console.error(error);
                setLoading(false);
                setError(true);
            },
        );
    }, [idUser, timezone?.idianazone]);

    useEffect(() => {
        dispatch(EntityListActions.setUseLazyLoad(AGENDA, true));
        return subscribe(`${ENTITY_MODAL_UPDATE}--${AGENDA.entity}`, () => getEvents());
    }, [dispatch, getEvents]);

    useEffect(() => {
        Context.serverListManager.getList('fm_iana_time_zone').then((data) => {
            const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const userTimezone = data.reduce((obj, current) => {
                if (current.ianazonenames?.includes(browserTimezone)) obj = current;
                return obj;
            }, {});
            if (Object.keys(userTimezone).length) {
                setTimezone(userTimezone);
            }
        });
    }, []);

    useEffect(() => {
        if (!timezone) return;
        getEvents();
    }, [getEvents, timezone]);

    const onViewMore = useCallback(() => {
        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'list',
        });
        window.open(getRoute(AGENDA.route), '_blank');
    }, []);

    const onAdd = useCallback(() => {
        const defaultRange = getPreviousAndNextDate(60);
        const hini = defaultRange.previous;
        const fini = new Date(hini);

        dispatch(
            EntityModalActions.init({
                entity: AGENDA,
                data: {
                    user: {
                        label: userName,
                        value: idUser,
                    },
                    fini,
                    hini,
                    idTimezone,
                    isCompletado: '0',
                    isTarea: '0',
                },
                labels: {
                    title: getLiteral('title_create_event'),
                    success: getLiteral('succes_entitycreatedsuccessfully'),
                    error: getLiteral('label_failed_insert_activity_salesforce'),
                },
                crudTab: AGENDA_EVENT,
                hideDelete: true,
            }),
        );

        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'create',
        });
    }, [dispatch, idTimezone, idUser, userName]);

    const onPopupVisibleChange = useCallback((isVisible) => {
        setIsDetailVisible(isVisible);
        if (popoverCallbacksRef?.current?.onPopupVisibleChange) {
            popoverCallbacksRef?.current?.onPopupVisibleChange(isVisible);
        }

        if (isVisible) {
            logEvent({
                event: 'dashboard',
                submodule: AGENDA.trueName,
                functionality: 'detailView',
            });
        }
    }, []);

    const onDeleteEvent = useCallback(() => {
        return new Promise((resolve, reject) => {
            const id = event.Id;
            Context.domainManager.deleteEntity(
                AGENDA.entity,
                id,
                () => {
                    getEvents();
                    resolve();
                },
                (error) => {
                    console.error(error);
                    reject();
                },
            );
        });
    }, [event?.Id, getEvents]);

    const onEditEvent = useCallback(
        (event) => {
            if (!event || event?.g_recurrence) return;
            const canEditItem = getBackendBoolean(event.CanEdit);
            if (!agendaPermission?.update || !(canEditItem && !event.isReadOnly)) {
                return;
            }

            dispatch(
                EntityModalActions.init({
                    entity: AGENDA,
                    id: event.Id,
                    labels: {
                        title: getLiteral('title_edit_appointment'),
                        success: getLiteral('succes_entityupdatedsuccessfully'),
                        error: getLiteral('error_generalerror'),
                        deleteModalTitle: getLiteral('title_delete_event'),
                        successDelete: getLiteral('succes_entitydeletedsuccessfully'),
                    },
                    hideDelete: !agendaPermission?.delete,
                    disableConfirm: !agendaPermission?.update,
                    crudTab: AGENDA_EVENT,
                }),
            );

            logEvent({
                event: 'dashboard',
                submodule: AGENDA.trueName,
                functionality: 'detailView',
            });
        },
        [agendaPermission?.delete, agendaPermission?.update, dispatch],
    );

    const onClickCompany = useCallback(
        (e) => {
            if (e.ctrlKey || !event) return true;

            e.stopPropagation();
            e.preventDefault();

            openTab({
                entity: COMPANIES,
                id: event.IdCompany,
                hasCrudInDetail: false,
                toTab: true,
                avoidRedirects: true,
            });
        },
        [event, openTab],
    );

    const onClickAddress = useCallback((e) => {
        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'route',
        });
    }, []);

    const emptyActions = useMemo(() => {
        let actions = [];

        const nylasStatus = nylasConfig?.account?.status || 0;
        if (syncCalendarWithNylas && nylasStatus <= 0) {
            actions.push(
                <Button
                    type="secondary"
                    size="small"
                    onClick={() => {
                        logEvent({
                            event: 'dashboard',
                            submodule: AGENDA.trueName,
                            functionality: 'syncEmailCal',
                        });
                        ensureRoute('/settings/nylas');
                    }}
                >
                    {getLiteral('action_sync_cal')}
                </Button>,
            );
        }

        return actions;
    }, [nylasConfig?.account?.status, syncCalendarWithNylas]);

    const attendeesList = useMemo(() => {
        return (
            <div className="fm-agenda-small-detail__attendees-content">
                <AttendeesList data={event} />
            </div>
        );
    }, [event]);

    const getAttendeeURL = useCallback((attendee) => {
        switch (attendee.AttendeeType) {
            case ATTENDEE_TYPES.contact:
                return getRoute(CONTACTS.route + `/${attendee.id}`);
            case ATTENDEE_TYPES.userAttendee:
                return getUserSfmUrl(attendee.id);
            case ATTENDEE_TYPES.externalAttendee:
                return `mailto:${attendee.Address}`;
        }
    }, []);

    const getAttendeeAvatar = useCallback((attendee) => {
        switch (attendee.AttendeeType) {
            case ATTENDEE_TYPES.contact:
                return (
                    <Avatar icon="singleUser" src={getSrcContactCircleAvatar(attendee.id).src} />
                );
            case ATTENDEE_TYPES.userAttendee:
                return <Avatar icon="singleUser" src={getSrcUserCircleAvatar(attendee.id).src} />;
            case ATTENDEE_TYPES.externalAttendee:
                return <Avatar icon="singleUser" />;
        }
    }, []);

    const upcomingEvent = useMemo(() => {
        if (!event || !timezone) return null;
        const canEditItem = getBackendBoolean(event.CanEdit);
        const canDeleteItem = getBackendBoolean(event.CanDelete);

        const isEventEditable =
            !event.g_recurrence && agendaPermission?.update && canEditItem && !event.isReadOnly;

        const isEventDeletable =
            !event.g_recurrence &&
            agendaPermission?.delete &&
            canEditItem &&
            canDeleteItem &&
            !event.isReadOnly;

        let popoverCustomProps = {};
        if (isEventEditable) {
            popoverCustomProps = {
                onEdit: () => onEditEvent(event),
                titleDelete: getLiteral('title_delete_event'),
                subtitleDelete: getLiteralWithParameters('confirm_delete', [`"${event.Asunto}"`]),
            };
        } else {
            let labelCantEdit = '';

            if (!agendaPermission.update || !canEditItem || event.isReadOnly) {
                labelCantEdit = getLiteral('label_calendar_no_edit_no_permission');
            }
            if (event.g_recurrence) {
                labelCantEdit = getLiteral('label_calendar_no_edit_recurrent');
            }

            popoverCustomProps = { labelCantEdit };
        }

        if (isEventDeletable) {
            popoverCustomProps.onDelete = () => onDeleteEvent(event);
        }

        const showAddress = event?.Direccion?.trim() && event.lat && event.lon;
        const showCompany = event.IdCompany !== '-1';

        const startDate = getDateToTimezone({
            date: event.fini,
            timezone: timezone.idianazone,
            returnMoment: true,
        });
        const endDate = getDateToTimezone({
            date: event.ffin,
            timezone: timezone.idianazone,
            returnMoment: true,
        });

        const firstSegmentDate = startDate.isSame(moment(), 'day')
            ? getLiteral('label_today')
            : getActivityDateFormat(startDate, true, false);

        return (
            <div className="fm-dashboard-widget__container fm-upcoming-next-event-widget fm-agenda-small-detail ">
                <Text type="subtitle" color={theme.colors.utility.textPrimary} medium>
                    {firstSegmentDate}, {startDate.format('HH:mm')} - {endDate.format('HH:mm')}
                </Text>
                <Text color={theme.colors.utility.textSecondary}>{event.Asunto || '-'}</Text>
                {showCompany && (
                    <>
                        <Spacer y={5} />
                        <CompanyChip
                            idCompany={event.IdCompany}
                            remotePopover={true}
                            onClickPopover={onClickCompany}
                        >
                            <div className="fm-upcoming-next-event-widget__row">
                                <CompanyAvatar id={event.IdCompany} />
                                <div className="fm-upcoming-next-event-widget__row-right">
                                    <Text type="caption" color={theme.colors.utility.textSecondary}>
                                        {getLiteral('label_account')}
                                    </Text>
                                    <Link
                                        href={getCompanyDetailUrl(event.IdCompany)}
                                        target="blank"
                                        onClick={onClickCompany}
                                    >
                                        {event.empresa}
                                    </Link>
                                </div>
                            </div>
                        </CompanyChip>
                    </>
                )}
                {showAddress && (
                    <>
                        <Spacer y={5} />
                        <div className="fm-upcoming-next-event-widget__row">
                            <Avatar icon="location" />
                            <div className="fm-upcoming-next-event-widget__row-right">
                                <Text type="caption" color={theme.colors.utility.textSecondary}>
                                    {getLiteral('label_address')}
                                </Text>
                                <Link
                                    href={`https://www.google.com/maps/search/?api=1&query=${event.lat},${event.lon}`}
                                    target="blank"
                                    isTruncated
                                    onClick={onClickAddress}
                                >
                                    {event.Direccion || '-'}
                                </Link>
                            </div>
                        </div>
                    </>
                )}
                {event.attendees?.length > 1 && (
                    <>
                        <Spacer y={5} />
                        <div className="fm-upcoming-next-event-widget__row">
                            <Avatar icon="multipleUsers" />
                            <div className="fm-upcoming-next-event-widget__row-right">
                                <Popover
                                    className="fm-attendees-popover"
                                    placement="bottom"
                                    content={attendeesList}
                                >
                                    <AttendeesField
                                        data={event}
                                        useDivider={false}
                                        withIcon={false}
                                    />
                                </Popover>
                            </div>
                        </div>
                    </>
                )}
                {event.attendees?.length === 1 && (
                    <>
                        <Spacer y={5} />
                        <div className="fm-upcoming-next-event-widget__row">
                            {getAttendeeAvatar(event.attendees[0])}
                            <div className="fm-upcoming-next-event-widget__row-right">
                                <Text type="caption" color={theme.colors.utility.textSecondary}>
                                    {getLiteral('label_attendee')}
                                </Text>
                                <Link
                                    href={getAttendeeURL(event.attendees[0])}
                                    target="blank"
                                    isTruncated
                                >
                                    {event.attendees[0].Name || event.attendees[0].Address || '-'}
                                </Link>
                            </div>
                        </div>
                    </>
                )}
                <Spacer y={5} />
                <div className="fm-dashboard-widget__footer">
                    <EntityDetailPopover
                        {...popoverCustomProps}
                        content={
                            <AgendaSmallDetail
                                data={event}
                                popoverCallbacks={popoverCallbacksRef.current}
                                isDetailVisible={isDetailVisible}
                            />
                        }
                        onPopupVisibleChange={onPopupVisibleChange}
                        entity={AGENDA}
                        entityId={event?.Id}
                    >
                        <Button type="secondary" icon="launch">
                            {getLiteral('action_view_detail_single')}
                        </Button>
                    </EntityDetailPopover>
                    <VideoCallField data={event} withCopy={false} withIcon={false} />
                </div>
            </div>
        );
    }, [
        event,
        timezone,
        agendaPermission.update,
        agendaPermission?.delete,
        onClickCompany,
        onClickAddress,
        attendeesList,
        getAttendeeAvatar,
        getAttendeeURL,
        isDetailVisible,
        onPopupVisibleChange,
        onEditEvent,
        onDeleteEvent,
        theme,
    ]);

    const rowList = useMemo(() => {
        return events?.map((item) => {
            const canEditItem = getBackendBoolean(item.CanEdit);
            const canDeleteItem = getBackendBoolean(item.CanDelete);

            const isEventEditable =
                !item.g_recurrence && agendaPermission?.update && canEditItem && !item.isReadOnly;

            const isEventDeletable =
                !item.g_recurrence &&
                agendaPermission?.delete &&
                canEditItem &&
                canDeleteItem &&
                !item.isReadOnly;

            let popoverCustomProps = {};
            if (isEventEditable) {
                popoverCustomProps = {
                    onEdit: () => onEditEvent(item),
                    titleDelete: getLiteral('title_delete_event'),
                    subtitleDelete: getLiteralWithParameters('confirm_delete', [
                        `"${item.Asunto}"`,
                    ]),
                };
            } else {
                let labelCantEdit = '';

                if (!agendaPermission.update || !canEditItem || item.isReadOnly) {
                    labelCantEdit = getLiteral('label_calendar_no_edit_no_permission');
                }
                if (item.g_recurrence) {
                    labelCantEdit = getLiteral('label_calendar_no_edit_recurrent');
                }

                popoverCustomProps = { labelCantEdit };
            }

            if (isEventDeletable) {
                popoverCustomProps.onDelete = () => onDeleteEvent(item);
            }

            return (
                <EntityDetailPopover
                    key={item.Id || item.id}
                    {...popoverCustomProps}
                    content={
                        <AgendaSmallDetail
                            data={item}
                            popoverCallbacks={popoverCallbacksRef.current}
                            isDetailVisible={isDetailVisible}
                        />
                    }
                    onPopupVisibleChange={onPopupVisibleChange}
                    entity={AGENDA}
                    entityId={item?.Id}
                >
                    <DashboardWidgetRow
                        item={item}
                        leftColumn={
                            <Icon name="event" size="large" color={theme.colors.grey[500]} />
                        }
                        title={item.Asunto || '-'}
                        subtitle={getActivityDateFormat(moment(item.fini))}
                    />
                </EntityDetailPopover>
            );
        });
    }, [
        agendaPermission?.delete,
        agendaPermission.update,
        events,
        isDetailVisible,
        onDeleteEvent,
        onEditEvent,
        onPopupVisibleChange,
        theme.colors.grey,
    ]);

    return (
        <DashboardWidget
            title={getLiteral('label_upcoming_event')}
            // State
            isLoading={loading}
            isEmpty={!event && !error && !events?.length}
            isError={error}
            // Actions
            onViewMore={onViewMore}
            onAdd={agendaPermission?.create ? onAdd : undefined}
            addTooltip={getLiteral('action_add_event')}
            // Empty view
            emptyTitle={getLiteral('label_empty_widget_events')}
            emptySubtitle={getLiteral('label_empty_widget_events_desc')}
            emptyImage={EMPTY_SVG}
            emptyActions={emptyActions}
        >
            {upcomingEvent}
            {!error && <DashboardWidgetHeader title={getLiteral('label_next_events')} />}
            {rowList}
        </DashboardWidget>
    );
});

export default UpcomingNextEventDashboardWidget;
